<template>
  <Modal :active="active" :title="$t('forms.appointment.title')" :vehicle="vehicle" @modalClosed="closeModal" ref="modal">
    <p v-if="successMsg" class="form-success">{{ successMsg }}</p>
    <p v-if="errorMsg" class="form-error">{{ errorMsg }}</p>
    <div class="form-modal-intro">
      <div v-if="introText" v-html="introText"></div>
    </div>
    <form id="book-appointment" @submit.prevent="submitForm($event)" @change="validateErrorFields">
      <p v-if="errors.length > 0" class="form-error">{{ $t('forms.errors.requiredFields') }}</p>

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label">{{ $t('forms.fields.salutation') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field formRow__field--text">
          <div class="form-radio form-radio--inline">
            <input id="offer-salutation-1" class="form-radio__input" type="radio" value="Dhr." v-model="salutation" />
            <label class="form-radio__label" for="offer-salutation-1">{{ $t('forms.fields.salutationMale') }}</label>
          </div>
          <!-- .form-radio -->

          <div class="form-radio form-radio--inline">
            <input id="offer-salutation-2" class="form-radio__input" type="radio" value="Mevr." v-model="salutation" />
            <label class="form-radio__label" for="offer-salutation-2">{{ $t('forms.fields.salutationFemale') }}</label>
          </div>
          <!-- .form-radio -->

          <p v-if="isError('salutation')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="buy-fname">{{ $t('forms.fields.name') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <div class="formRow__field__grid">
            <div class="formRow__field__grid__column">
              <input
                id="buy-fname"
                class="form-textField"
                :class="{ 'form-textField--error': isError('fname') }"
                type="text"
                v-model="fname"
                :placeholder="$t('forms.fields.firstName')"
              />
              <p v-if="isError('fname')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
            </div>
            <!-- .formRow__field__grid__column -->
            <div class="formRow__field__grid__column">
              <input
                id="buy-lname"
                class="form-textField"
                :class="{ 'form-textField--error': isError('lname') }"
                type="text"
                v-model="lname"
                :placeholder="$t('forms.fields.lastName')"
              />
              <p v-if="isError('lname')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
            </div>
            <!-- .formRow__field__grid__column -->
          </div>
          <!-- .formRow__field__grid -->
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="appointment-email">{{ $t('forms.fields.email') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <input
            id="appointment-email"
            class="form-textField"
            :class="{ 'form-textField--error': isError('email') }"
            type="email"
            v-model="email"
          />
          <p v-if="isError('email')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="testRide-phone">{{ $t('forms.fields.phone') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <input
            id="testRide-phone"
            class="form-textField"
            :class="{ 'form-textField--error': isError('phone') }"
            type="text"
            v-model="phone"
          />
          <p v-if="isError('phone')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="testRide-preferredDate">{{ $t('forms.appointment.preferenceDate') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <datepicker
            id="testRide-preferredDate"
            class="form-dateField"
            :class="{ 'form-dateField--error': isError('preferredDate') }"
            :format="'d MMMM yyyy'"
            :language="datepickerLanguage"
            :disabled-dates="{ to: new Date() }"
            :monday-first="true"
            v-model="preferredDate"
          ></datepicker>
          <p v-if="isError('preferredDate')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>

      <!-- .formRow -->
      <div class="formRow">
        <div class="formRow__label">
          <label class="form-label" for="testRide-preferredTime">{{ $t('forms.appointment.preferenceTime') }}</label>
        </div>
        <!-- .formRow__label -->
        <div class="formRow__field">
          <div class="formRow__timepicker">
            <select v-model="preferredTimeHours" class="form-select">
              <option>08</option>
              <option>09</option>
              <option>10</option>
              <option>11</option>
              <option>12</option>
              <option>13</option>
              <option>14</option>
              <option>15</option>
              <option>16</option>
              <option>17</option>
            </select>
            <div>:</div>
            <select v-model="preferredTimeMinutes" class="form-select">
              <option>00</option>
              <option>15</option>
              <option>30</option>
              <option>45</option>
            </select>
          </div>
          <p v-if="isError('preferredTime')" class="form-fieldError">{{ $t('forms.errors.requiredFieldSingle') }}</p>
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow formRow--spaced">
        <div class="formRow__field formRow__field--noLabel">
          <div class="form-checkbox">
            <input id="testRide-acceptTerms" class="form-checkbox__input" type="checkbox" v-model="acceptTerms" />
            <label class="form-checkbox__label" for="testRide-acceptTerms">
              <span
                >{{ $t('forms.fields.acceptTermsPre') }} <a :href="termsUrl" target="_blank">{{ $t('forms.fields.terms') }}</a></span
              >
            </label>
            <p v-if="isError('acceptTerms')" class="form-fieldError form-fieldError--spaced">
              {{ $t('forms.errors.acceptTerms') }}
            </p>
          </div>
          <!-- .form-checkbox -->
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->

      <div class="formRow formRow--spaced">
        <div class="formRow__field formRow__field--noLabel">
          <input
            type="submit"
            class="button button--primary button--arrow button--submit"
            :class="{ 'button--loading': loading }"
            :disabled="loading"
            :value="$t('forms.appointment.submit')"
          />
        </div>
        <!-- .formRow__field -->
      </div>
      <!-- .formRow -->
    </form>
  </Modal>
</template>

<script>
import { WPSettings } from '@/WPSettings'
import axios from 'axios'
import Modal from '@/components/Modal'
import Datepicker from '@sum.cumo/vue-datepicker'
import '@sum.cumo/vue-datepicker/dist/Datepicker.css'
import { nl, en, fr, de } from '@sum.cumo/vue-datepicker/dist/locale/index.esm'
import { format } from 'date-fns'

export default {
  name: 'modalTestRide',
  data() {
    return {
      loading: false,
      errors: [],
      errorMsg: '',
      successMsg: '',
      salutation: '',
      fname: '',
      lname: '',
      email: '',
      phone: '',
      preferredDate: '',
      preferredTimeHours: '08',
      preferredTimeMinutes: '00',
      acceptTerms: false,
      termsUrl: this.$store.state.settings.general.termsUrl,
    }
  },
  props: ['active', 'introText', 'vehicle'],
  components: {
    Modal,
    Datepicker,
  },
  computed: {
    currentVehicleId() {
      const currentVehicle = this.$store.getters['vehicles/current']
      return currentVehicle ? currentVehicle.id : null
    },
    preferredTime() {
      return `${this.preferredTimeHours}:${this.preferredTimeMinutes}`
    },
    datepickerLanguage() {
      switch (WPSettings.LANGUAGE) {
        case 'en':
          return en
        case 'fr':
          return fr
        case 'de':
          return de
        default:
          return nl
      }
    },
  },
  methods: {
    closeModal() {
      // Emit to parent component
      this.$emit('modalClosed', true)

      // Reset errors - just because it's prettier
      this.errors = []
    },

    isError(field) {
      // Check if field is in the error list
      return this.errors.includes(field)
    },

    validateErrorFields() {
      // Re-check the error fields
      if (this.errors.length > 0) {
        this.errors.forEach((error) => {
          if (this[error]) {
            // Field has a value, so remove it from the error list
            this.errors = this.errors.filter((el) => el !== error)
          }
        })
      }
    },

    validateForm() {
      // Reset errors
      this.errors = []

      // Fields to check
      const checkIfHasValue = ['salutation', 'fname', 'lname', 'email', 'phone', 'preferredDate', 'preferredTime', 'acceptTerms']

      // Check fields
      checkIfHasValue.forEach((field) => {
        if (!this[field]) {
          // Field has no value, so add it to error list
          this.errors.push(field)
        }
      })
    },

    submitForm(e) {
      // Prevent form from being submitted by browser
      e.preventDefault()

      // Validate form
      this.validateForm()

      // If there are any errors, stop here
      if (this.errors.length > 0) {
        return false
      }

      // Do the call to WP REST
      this.loading = true

      axios
        .post(
          WPSettings.API_URL + '/form-appointment',
          {
            vehicleId: this.currentVehicleId,
            salutation: this.salutation,
            fname: this.fname,
            lname: this.lname,
            email: this.email,
            phone: this.phone,
            preferredDate: this.formatDateForApi(this.preferredDate),
            preferredTime: this.preferredTime,
          },
          {
            headers:
              process.env.NODE_ENV === 'production'
                ? {
                    'X-WP-Nonce': WPSettings.NONCE,
                  }
                : {},
          }
        )
        .then((response) => {
          if (response.data.status === 'success') {
            // Update success message
            this.errorMsg = ''
            this.successMsg = response.data.message

            // Reset form
            this.salutation =
              this.fname =
              this.lname =
              this.email =
              this.phone =
              this.preferredDate =
              this.preferredTimeHours =
              this.preferredTimeMinutes =
              this.acceptTerms =
                ''
          }
        })
        .catch((error) => {
          if (error.response.data.status === 'error') {
            // Update error message
            this.successMsg = ''
            this.errorMsg = error.response.data.message
          } else {
            this.successMsg = ''
            this.errorMsg = this.$t('forms.errors.somethingWentWrong')
          }
        })
        .finally(() => (this.loading = false))
    },
    formatDateForApi(date) {
      if (!date) {
        return null
      }

      return format(date, 'dd/MM/yyyy')
    },
  },
}
</script>
